import {
  Injectable,
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
  ComponentRef,
  Type,
  inject,
} from '@angular/core';

import { Subject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { ModalVariant } from '@shared-enums';

import { VOneModalComponent } from '../../components/v-one-modal/v-one-modal.component';
import { ModalConfig, ModalResult } from '../../models';

/**
 * Service for programmatically opening modals with dynamic components
 * Provides a clean API similar to MatDialog but using v-one-modal
 */
@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);

  private activeModals = new Map<string, ComponentRef<VOneModalComponent>>();
  private modalCounter = 0;

  /**
   * Opens a modal with a dynamic component
   * @param component - Angular component to render inside the modal
   * @param config - Modal configuration (title, variant, etc.)
   * @param inputs - Optional inputs to pass to the component
   * @returns Observable that emits when the modal closes
   *
   * @example
   * this.modalService.openComponent(
   *   SalesRegisterFormComponent,
   *   { title: 'Register Sale', variant: 'dialog' },
   *   { customerId: 123 }
   * ).subscribe(result => {
   *   console.log('Modal closed with result:', result);
   * });
   */
  openComponent<T = unknown>(
    component: Type<unknown>,
    config: Partial<ModalConfig> = {},
    inputs?: Record<string, unknown>,
  ): Observable<T> {
    const modalId = `modal-${++this.modalCounter}`;

    // Create modal component
    const modalRef = createComponent(VOneModalComponent, {
      environmentInjector: this.injector,
    });

    // Configure modal
    const fullConfig: ModalConfig = {
      title: config.title || '',
      variant: config.variant || ModalVariant.DIALOG,
      showClose: config.showClose ?? true,
      showFooter: false, // No footer by default with components
      ...config,
    };

    modalRef.setInput('visible', true);
    modalRef.setInput('component', component);
    modalRef.setInput('componentInputs', {
      ...inputs,
      // Lets the loaded component's own close/cancel button trigger the
      // same animated close the wrapper's "X" button uses, instead of each
      // component reimplementing (or forgetting to wire) that teardown.
      // Opt-in: components that don't declare a matching @Input() just
      // ignore it.
      closeModal: () => modalRef.instance.handleClose(),
    });
    modalRef.setInput('config', fullConfig);

    const subject = new Subject<T>();

    const visibleChangeSub = modalRef.instance.visibleChange.subscribe(
      (visible: boolean) => {
        if (!visible) {
          modalRef.setInput('visible', false);

          subject.next(undefined as T);
          subject.complete();
          cleanup();
        }
      },
    );

    this.activeModals.set(modalId, modalRef);

    const cleanup = () => {
      visibleChangeSub.unsubscribe();
      this.destroyModal(modalId);
    };

    this.appRef.attachView(modalRef.hostView);
    const domElem = (modalRef.hostView as unknown as { rootNodes: unknown[] })
      .rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    return subject.asObservable().pipe(take(1));
  }

  /**
   * Opens a simple modal with message (compatibility with AlertService)
   * @param config - Complete modal configuration
   * @returns Observable that emits when the modal closes
   *
   * @example
   * this.modalService.open({
   *   title: 'Success',
   *   message: 'Operation completed',
   *   variant: 'success'
   * }).subscribe(() => {
   *   console.log('Modal closed');
   * });
   */
  open<T = unknown>(config: ModalConfig): Observable<ModalResult<T>> {
    const modalId = `modal-${++this.modalCounter}`;

    const modalRef = createComponent(VOneModalComponent, {
      environmentInjector: this.injector,
    });

    modalRef.setInput('visible', true);
    modalRef.setInput('config', config);

    const subject = new Subject<ModalResult<T>>();

    const closeSub = modalRef.instance.closed.subscribe(() => {
      subject.next({ action: 'close' });
      subject.complete();
      cleanup();
    });

    const acceptSub = modalRef.instance.accept.subscribe(() => {
      subject.next({ action: 'accept' });
      subject.complete();
      cleanup();
    });

    const rejectSub = modalRef.instance.reject.subscribe(() => {
      subject.next({ action: 'reject' });
      subject.complete();
      cleanup();
    });

    this.activeModals.set(modalId, modalRef);

    const cleanup = () => {
      closeSub.unsubscribe();
      acceptSub.unsubscribe();
      rejectSub.unsubscribe();
      this.destroyModal(modalId);
    };

    this.appRef.attachView(modalRef.hostView);
    const domElem = (modalRef.hostView as unknown as { rootNodes: unknown[] })
      .rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    return subject.asObservable().pipe(take(1));
  }

  /**
   * Closes all open modals
   */
  closeAll(): void {
    Array.from(this.activeModals.keys()).forEach((modalId) => {
      this.destroyModal(modalId);
    });
    this.activeModals.clear();
  }

  /**
   * Destroys a specific modal instance
   */
  private destroyModal(modalId: string): void {
    const modalRef = this.activeModals.get(modalId);
    if (!modalRef) return;

    const domElem = (modalRef.hostView as unknown as { rootNodes: unknown[] })
      .rootNodes[0] as HTMLElement;
    if (domElem && domElem.parentNode) {
      domElem.parentNode.removeChild(domElem);
    }

    this.appRef.detachView(modalRef.hostView);
    modalRef.destroy();
    this.activeModals.delete(modalId);
  }
}
